import { useState } from 'react'
import { Outlet, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import {
  Add as AddIcon,
  Kanban as KanbanIcon,
  Table as TableIcon,
} from '@factorialco/f0-react/icons/app'
import { Buffer } from 'buffer'
import { ProjectTasksDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ProjectManagementProjectDetailsPageProjectTasksPageDocument,
  ProjectManagementProjectDetailsPageQuery,
  ProjectTaskAnalyticsDocument,
  ProjectTasksDataCollectionDocument,
  ProjectTasksKanbanDocument,
  ProjectTasksKanbanQuery,
  ProjectTasksKanbanQueryVariables,
  UpdateProjectTaskDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import Kanban from 'modules/project_management/components/Kanban'
import {
  KanbanColumnData,
  KanbanColumnType,
} from 'modules/project_management/components/Kanban/Column'
import { DragItem } from 'modules/project_management/components/Kanban/utils'
import ProjectTaskAnalytics from 'modules/project_management/components/ProjectTaskAnalytics'
import { SegmentedControl } from 'modules/project_management/components/SegmentedControl'
import ProjectTasksDataCollection from 'modules/project_management/components/tables/ProjectTasksDataCollection'
import ProjectTasksDatatable from 'modules/project_management/components/tables/TasksDataTable'
import { TaskSingleActions } from 'modules/project_management/components/tables/TasksDataTable/Actions'
import { UpsellProjectsTasks } from 'modules/project_management/components/upsells/UpsellProjectsTasks'
import { AddTaskWizard } from 'modules/project_management/components/wizards/AddTaskWizard'
import { isValidTaskStatus } from 'modules/tasks/lib/status'

import { kanbanProjections } from './kanbanProjections'

type KanbanProjectTask =
  ProjectTasksKanbanQuery['projectManagement']['projectTasksConnection']['edges'][0]['node']
interface UpdateTaskContext {
  sourceStatus: string
  subproject?: KanbanProjectTask['subproject'] | null
  taskId?: number | null
  assignees?: {
    __typename?: 'TasksUnsafeAccessConnection'
    edges: Array<{
      __typename?: 'TasksUnsafeAccessEdge'
      node: {
        __typename?: 'TasksUnsafeAccess'
        id: number
        avatar?: string | null
        firstName: string
        lastName: string
      }
    }>
  } | null
  attachments?: {
    __typename?: 'ApiCoreAttachmentConnection'
    totalCount?: number | null
  } | null
  comments?: {
    __typename?: 'CommentsCommentConnection'
    totalCount?: number | null
  } | null
}

const ProjectTasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { id: projectId } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data: pageData } = useQuery(ProjectManagementProjectDetailsPageProjectTasksPageDocument, {
    variables: {
      companyId,
      projectId: Number(projectId),
    },
  })

  const dataCollectionsEnabled =
    pageData?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_data_collections'
    )?.enabled ?? false

  const hasProjectWorkers =
    (pageData?.projectManagement?.projectWorkersConnection?.totalCount ?? 0) > 0

  const { hasProjectTasks, hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasProjectTasks: boolean
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: NonNullable<
      ProjectManagementProjectDetailsPageQuery['projectManagement']['projectsConnection']['nodes'][0]
    >
  }>()

  const canManageTasks = pageData?.projectManagement?.project?._policies?.manageTasks?.can ?? false
  const hasTimeTrackingSection =
    pageData?.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can ?? false

  const canExportData =
    canManageTasks && (hasTimeTrackingSection || hasExpenses || hasFlexibleTracking)

  const columnAction = {
    icon: AddIcon,
    label: i18n.t('project_management.tasks.add_kanban_column_button'),
    onClick: (columnId: string) => {
      track(trackingEvents.projectManagement.tasks.createProjectTaskButton, {
        origin: 'kanban column',
      })
      setIsWizardOpen(true)
      setTaskStatusCreation(columnId)
    },
  }

  useEffect(() => {
    track(trackingEvents.projectManagement.tasks.visitProjectTasks)
  }, [])

  const view = searchParams.get('view')
  const initialView: 'table' | 'kanban' = view === 'kanban' ? 'kanban' : 'table'
  const [selectedView, setSelectedView] = useState<'table' | 'kanban'>(initialView)
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [taskStatusCreation, setTaskStatusCreation] = useState<string>('')

  const [updateTask] = useMutation(UpdateProjectTaskDocument, {
    extractorFn: (mutationData) =>
      mutationData.projectManagementMutations.updateProjectmanagementProjectTask,
    update: (cache, { data }, { context }: { context?: UpdateTaskContext }) => {
      const updatedProjectTask =
        data?.projectManagementMutations.updateProjectmanagementProjectTask.projectTask
      if (!updatedProjectTask) return

      const sourceStatus = context?.sourceStatus
      const destinationStatus = updatedProjectTask.task?.status
      const subproject = context?.subproject ?? null
      const assigneesConnection = context?.assignees ?? null
      const attachmentsConnection = context?.attachments ?? null
      const commentsConnection = context?.comments ?? null

      // Helper function to update a column's cache
      const updateColumnCache = (
        status: string,
        shouldRemoveTask: boolean,
        shouldAddTask: boolean
      ) => {
        cache.updateQuery(
          {
            query: ProjectTasksKanbanDocument,
            variables: { companyId, projectId: project.id, status },
          },
          (cachedData) => {
            if (!cachedData) return

            let edges = [...cachedData.projectManagement.projectTasksConnection.edges]

            // Remove task from this column if needed
            if (shouldRemoveTask) {
              edges = edges.filter((edge) => edge.node.id !== updatedProjectTask.id)
            }

            // Add task to this column if needed
            if (shouldAddTask) {
              const newEdge = {
                __typename: 'ProjectManagementProjectTaskEdge' as const,
                cursor: Buffer.from(String(updatedProjectTask.id)).toString('base64'),
                node: {
                  __typename: 'ProjectManagementProjectTask' as const,
                  id: updatedProjectTask.id,
                  position: updatedProjectTask.position,
                  subproject: subproject ?? null,
                  task: {
                    __typename: 'TasksTask' as const,
                    id: context?.taskId || 0,
                    name: updatedProjectTask.task?.name || '',
                    status: updatedProjectTask.task?.status,
                    dueOn: updatedProjectTask.task?.dueOn,
                    startsOn: updatedProjectTask.task?.startsOn,
                    assigneesConnection: assigneesConnection || {
                      __typename: 'TasksUnsafeAccessConnection' as const,
                      edges: [],
                    },
                    attachmentsConnection: attachmentsConnection || {
                      __typename: 'ApiCoreAttachmentConnection' as const,
                      totalCount: 0,
                    },
                    commentsConnection: commentsConnection || {
                      __typename: 'CommentsCommentConnection' as const,
                      totalCount: 0,
                    },
                  },
                },
              }
              edges.push(newEdge)
            }

            // Sort edges by position
            edges = edges.sort((a, b) => {
              const posA = a.node.position || ''
              const posB = b.node.position || ''
              return posA.localeCompare(posB)
            })

            return {
              ...cachedData,
              projectManagement: {
                ...cachedData.projectManagement,
                projectTasksConnection: {
                  ...cachedData.projectManagement.projectTasksConnection,
                  edges,
                  totalCount: edges.length,
                  pageInfo: {
                    ...cachedData.projectManagement.projectTasksConnection.pageInfo,
                    endCursor: edges[edges.length - 1]?.cursor || null,
                  },
                },
              },
            }
          }
        )
      }

      // Update source column (remove task)
      if (sourceStatus && sourceStatus !== destinationStatus) {
        updateColumnCache(sourceStatus, true, false)
      }

      // Update destination column (add task or update existing)
      if (destinationStatus) {
        updateColumnCache(destinationStatus, sourceStatus === destinationStatus, true)
      }
    },
    optimisticResponse: (vars) => {
      return {
        projectManagementMutations: {
          updateProjectmanagementProjectTask: {
            errors: [],
            projectTask: {
              id: vars.id,
              position: getOptimisticPosition(
                vars.afterPosition ?? null,
                vars.beforePosition ?? null
              ),
              projectId: vars.projectId,
              subproject: vars.subprojectId
                ? {
                    id: vars.subprojectId,
                    name: '',
                  }
                : null,
              task: {
                id: vars.id,
                name: vars.name,
                status: vars.status,
                dueOn: vars.dueOn,
                startsOn: vars.startsOn,
              },
            },
          },
        },
      }
    },
  })

  const getOptimisticPosition = (afterPosition: string | null, beforePosition: string | null) => {
    if (beforePosition && afterPosition) {
      if (beforePosition.length < afterPosition.length) return beforePosition + 'A'
      return beforePosition + 'L'
    }
    if (afterPosition) return 'A' + afterPosition
    if (beforePosition) return beforePosition + 'B'
    return 'B'
  }

  const handleDrop = async (
    originalItem: DragItem<ProjectTasksKanbanQueryVariables, KanbanProjectTask>,
    dropColumn: ProjectTasksKanbanQueryVariables,
    beforePosition?: string | null,
    afterPosition?: string | null
  ) => {
    const status = dropColumn.status
    if (!isValidTaskStatus(status)) return

    await updateTask({
      variables: {
        id: +originalItem.id,
        status,
        name: originalItem.title,
        projectId: originalItem.sourceColumnVariables.projectId,
        beforePosition,
        afterPosition,
        subprojectId: originalItem.item?.subproject?.id,
        dueOn: originalItem.item?.task?.dueOn,
        startsOn: originalItem.item?.task?.startsOn,
      },
      context: {
        sourceStatus: originalItem.sourceColumnVariables.status,
        subproject: originalItem.item?.subproject,
        taskId: originalItem.item?.task?.id,
        assignees: originalItem.item?.task?.assigneesConnection,
        attachments: originalItem.item?.task?.attachmentsConnection,
        comments: originalItem.item?.task?.commentsConnection,
      },
    })

    track(trackingEvents.projectManagement.tasks.updateTaskStatus, {
      mark_as: `Mark as ${dropColumn.status}`,
      number: 1,
      identifier: 'project',
      path_page: 'project tasks',
      origin: 'kanban',
    })
  }

  const columns: KanbanColumnType<ProjectTasksKanbanQueryVariables>[] = [
    {
      id: 'todo',
      title: i18n.t('tasks.status.todo'),
      statusColor: 'neutral',
      variables: { companyId: companyId, projectId: project.id, status: 'todo' },
    },
    {
      id: 'in_progress',
      title: i18n.t('tasks.status.in_progress'),
      statusColor: 'info',
      variables: { companyId: companyId, projectId: project.id, status: 'in_progress' },
    },
    {
      id: 'done',
      title: i18n.t('tasks.status.done'),
      statusColor: 'positive',
      variables: { companyId: companyId, projectId: project.id, status: 'done' },
    },
    {
      id: 'discarded',
      title: i18n.t('tasks.status.discarded'),
      statusColor: 'warning',
      variables: { companyId: companyId, projectId: project.id, status: 'discarded' },
    },
  ]

  const parseData = (
    data: ProjectTasksKanbanQuery | undefined
  ): KanbanColumnData<KanbanProjectTask> => {
    return {
      items:
        data?.projectManagement.projectTasksConnection.edges.map(({ node: projectTask }) => ({
          id: projectTask.id.toString() || '',
          title: projectTask.task?.name || '',
          position: projectTask.position,
          item: projectTask,
          projections: kanbanProjections(projectTask),
          onClickCard: () => {
            navigate(
              `${navigation.tasks.taskIdAndIdentifier.fullPath({
                taskId: projectTask.task?.id ?? 0,
                identifier: 'project',
              })}?view=${selectedView}`,
              {
                replace: true,
              }
            )
          },
        })) || [],
      totalCount: data?.projectManagement.projectTasksConnection.totalCount || 0,
    }
  }

  const dataSource = (data: ProjectTasksKanbanQuery) => {
    const connection = data?.projectManagement.projectTasksConnection
    return {
      edges:
        connection?.edges.map((edge) => ({
          cursor: edge.cursor ?? '',
          node: edge.node,
        })) ?? [],
      pageInfo: {
        hasNextPage: connection?.pageInfo.hasNextPage ?? false,
        endCursor: connection?.pageInfo.endCursor ?? '',
      },
    }
  }

  const segments = [
    {
      label: (
        <Box flexDirection="row" gap="s12" alignItems="center">
          <F0Icon icon={TableIcon} size="sm" />
          <Text size="200" weight="semibold" color="grey1000">
            {i18n.t('project_management.tasks.table.views.table')}
          </Text>
        </Box>
      ),
    },
    {
      label: (
        <Box flexDirection="row" gap="s12" alignItems="center">
          <F0Icon icon={KanbanIcon} size="sm" />
          <Text size="200" weight="semibold" color="grey1000">
            {i18n.t('project_management.tasks.table.views.kanban')}
          </Text>
        </Box>
      ),
    },
  ]

  const onChangeView = (index: number) => {
    const newView = index === 0 ? 'table' : 'kanban'
    setSelectedView(newView)
    setSearchParams({ view: newView }, { replace: true })

    track(trackingEvents.projectManagement.tasks.switchView, {
      to: newView,
    })
  }

  const segmentedControl = (
    <>
      {/* Here I do the conditional because, if not, it will put a big gap between the search button and the segmented control */}
      <SegmentedControl
        initialSegment={selectedView === 'table' ? 0 : 1}
        onChangeSelected={onChangeView}
        segments={segments}
      />
    </>
  )

  if (!hasProjectTasks) {
    return <UpsellProjectsTasks />
  }

  return (
    <Box paddingBottom="s0">
      {!dataCollectionsEnabled && <ProjectTaskAnalytics projectId={project.id} />}
      {!dataCollectionsEnabled && (
        <Box paddingTop="s24">
          <Text size="400" weight="semibold" color="grey1000">
            {i18n.t('project_management.tasks.tasks_tab')}
          </Text>
        </Box>
      )}
      {dataCollectionsEnabled ? (
        <ProjectTasksDataCollection
          projectId={project.id}
          projectName={project.name}
          canManageTasks={canManageTasks}
          canExportData={canExportData}
          onClickCreateTask={(newStatus) => {
            setIsWizardOpen(true)
            setTaskStatusCreation(newStatus ?? '')
          }}
        />
      ) : (
        <>
          {selectedView === 'table' && (
            <ProjectTasksDatatable
              projectId={project.id}
              segmentedControl={segmentedControl}
              currentView={selectedView}
              canManageTasks={canManageTasks}
              onClickCreateTask={() => setIsWizardOpen(true)}
            />
          )}
          {selectedView === 'kanban' && (
            <Kanban
              actions={
                <TaskSingleActions
                  fromKanban
                  onClickCreateTask={() => setIsWizardOpen(true)}
                  segmentedControl={segmentedControl}
                />
              }
              columnAction={columnAction}
              columns={columns}
              dataSource={dataSource}
              document={ProjectTasksKanbanDocument}
              handleDrop={handleDrop}
              parseData={parseData}
            />
          )}
        </>
      )}
      {isWizardOpen && (
        <AddTaskWizard
          hasProjectWorkers={hasProjectWorkers}
          onClose={() => {
            setIsWizardOpen(false)
            setTaskStatusCreation('')
          }}
          refetchQueries={[
            ProjectTaskAnalyticsDocument,
            dataCollectionsEnabled
              ? ProjectTasksDataCollectionDocument
              : selectedView === 'kanban'
                ? ProjectTasksKanbanDocument
                : ProjectTasksDataTableDocument,
          ]}
          taskStatus={taskStatusCreation}
        />
      )}
      <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
    </Box>
  )
}

export default ProjectTasksPage
